'use client'

/**
 * Deferred global overlay chrome, split out of the initial bundle.
 *
 * These widgets are floating, interaction-triggered chrome, background tasks or trackers that render
 * nothing above the fold (GuestEngagementNudge/PushPrompt return null until a condition fires;
 * AlertsChatDock and FeedbackWidget are fixed buttons the user opens; DailyBonusProvider, BuildVersion,
 * DeployReloadPrompt, ToastToaster and ChartClickTracker have no first-view output). They were imported
 * statically into app/layout.tsx, so their JS rode in the first-load bundle on EVERY page (layout.js is
 * the single largest chunk, ~62 KB gz on 2026-10-21) and they hydrated on the critical path even though
 * nothing needs them at first paint. The chat dock alone pulls ChatUserCard, CleanProfileFrame and the
 * chat image hooks.
 *
 * next/dynamic with ssr:false moves each one into its own async chunk. Nothing visible changes (they SSR
 * to nothing today), there is no CLS, and the first-load JS + TBT drop site-wide.
 *
 * ⏱ AND THEY WAIT FOR THE PAGE TO SETTLE (merged 2026-10-22). A plain ssr:false chunk is still fetched
 * DURING hydration, and on slow 4G that download competes with the page's own scripts. So every widget
 * here mounts after the window `load` event, once the browser is idle (2.5s at the latest), all at the
 * same moment. Measured on the live homepage before (mobile throttling, slow 4G + 4x CPU): 275 KB of JS,
 * 3.3s of script CPU, the layout chunk with only 44% of its code executed during load.
 *
 * ⛔ Do NOT add anything VISIBLE in the first view (the banners, SubBannerSlot), anything that GATES content
 * (SiteAccessGate), or anything that must see the very first moments of a visit (AnalyticsTracker,
 * ReferralBeacon). Deferring those would shift the layout, flash gated content, or lose attribution.
 *
 * ssr:false requires a client boundary, which is why this module is 'use client' and layout.tsx (a server
 * component) imports from here. See register mobile-first-paint-1021 (lever: defer first-load hydration)
 * and the Navbar overlays deferred the same way on 2026-10-10 (defer-navbar-overlays-first-load-1010).
 */
import dynamic from 'next/dynamic'
import { useEffect, useState, type ComponentType } from 'react'

let settled: Promise<void> | null = null

/** Resolves once, for every widget together: after `load`, when the browser is idle. */
function whenPageSettled(): Promise<void> {
  if (!settled) {
    settled = new Promise<void>((resolve) => {
      const idle = () => {
        const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback
        if (ric) ric(() => resolve(), { timeout: 2500 })
        else setTimeout(resolve, 200)
      }
      if (document.readyState === 'complete') idle()
      else window.addEventListener('load', idle, { once: true })
    })
  }
  return settled
}

function afterSettle(load: () => Promise<{ default: ComponentType }>): ComponentType {
  const Lazy = dynamic(load, { ssr: false })
  function Deferred() {
    const [ready, setReady] = useState(false)
    useEffect(() => {
      let live = true
      whenPageSettled().then(() => { if (live) setReady(true) })
      return () => { live = false }
    }, [])
    return ready ? <Lazy /> : null
  }
  return Deferred
}

export const AlertsChatDock = afterSettle(() => import('./AlertsChatDock'))
export const FeedbackWidget = afterSettle(() => import('./FeedbackWidget'))
export const GuestEngagementNudge = afterSettle(() => import('./GuestEngagementNudge'))
export const PushPrompt = afterSettle(() => import('./PushPrompt'))
export const DailyBonusProvider = afterSettle(() => import('../../components/DailyBonusProvider'))
export const BuildVersion = afterSettle(() => import('./BuildVersion'))
export const DeployReloadPrompt = afterSettle(() => import('./DeployReloadPrompt'))
export const ToastToaster = afterSettle(() => import('./ToastToaster'))
export const ChartClickTracker = afterSettle(() => import('./ChartClickTracker'))
